<!--
  - This file is part of Solana Reference Stake Pool code.
  -
  - Copyright © 2023, mFactory GmbH
  -
  - Solana Reference Stake Pool is free software: you can redistribute it
  - and/or modify it under the terms of the GNU Affero General Public License
  - as published by the Free Software Foundation, either version 3
  - of the License, or (at your option) any later version.
  -
  - Solana Reference Stake Pool is distributed in the hope that it
  - will be useful, but WITHOUT ANY WARRANTY; without even the implied
  - warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  - See the GNU Affero General Public License for more details.
  -
  - You should have received a copy of the GNU Affero General Public License
  - along with this program.
  - If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
  -
  - You can be released from the requirements of the Affero GNU General Public License
  - by purchasing a commercial license. The purchase of such a license is
  - mandatory as soon as you develop commercial activities using the
  - Solana Reference Stake Pool code without disclosing the source code of
  - your own applications.
  -
  - The developer of this program can be contacted at <info@mfactory.ch>.
  -->

<script setup lang="ts">
import { evaClose } from '@quasar/extras/eva-icons'
import { DEFAULT_EXACT_FEE, PriorityLevel, PriorityMode } from '../constants'
import { usePriorityFee } from '../index'
import { onlyNumber } from '../utils'

const priorityFee = usePriorityFee()
const dialog = ref(false)

const fee = computed(() => {
  if (priorityFee.mode === PriorityMode.ExactFee) {
    return priorityFee.exactFee
  }
  return priorityFee.level
})

const modes = [
  { label: 'Max Cap', value: PriorityMode.MaxCap },
  { label: 'Exact Fee', value: PriorityMode.ExactFee },
]

const levels = [
  { label: 'Fast', value: PriorityLevel.Fast },
  { label: 'Turbo', value: PriorityLevel.Turbo },
  { label: 'Ultra', value: PriorityLevel.Ultra },
]

function submit() {
  dialog.value = false
  if (priorityFee.exactFee <= 0) {
    priorityFee.exactFee = DEFAULT_EXACT_FEE
  }
}
</script>

<template>
  <div class="priority-fee-wrap">
    <div class="priority-fee">
      <q-btn
        :ripple="false"
        rounded
        unelevated
        @click="dialog = true"
      >
        Priority fee: &nbsp; <span class="text-secondary">{{ fee }}</span>
      </q-btn>
    </div>
  </div>
  <q-dialog
    v-model="dialog"
    transition-duration="150"
    transition-show="fade"
    transition-hide="fade"
  >
    <q-card class="dialog" style="width: 500px">
      <q-card-section class="relative-position">
        <div class="text-h6 text-center">
          Global Priority Fee
        </div>
        <q-btn
          padding="md"
          color="transparent"
          text-color="primary-gray"
          unelevated
          :icon="evaClose"
          class="absolute-right"
          @click="dialog = false"
        />
      </q-card-section>
      <q-separator />
      <q-card-section v-if="priorityFee.mode === PriorityMode.MaxCap">
        <div class="q-mb-sm">
          <b>Priority level:</b>
        </div>
        <q-btn-toggle
          v-model="priorityFee.level"
          spread
          rounded
          color="primary"
          toggle-color="accent"
          :options="levels"
          :ripple="false"
        />
      </q-card-section>
      <q-card-section>
        <div class="q-mb-sm">
          <b>Priority Mode:</b>
        </div>
        <q-btn-toggle
          v-model="priorityFee.mode"
          rounded
          color="primary"
          toggle-color="accent"
          size="sm"
          :options="modes"
          :ripple="false"
        />
      </q-card-section>
      <q-card-section>
        <template v-if="priorityFee.mode === PriorityMode.MaxCap">
          <b>Set Max Cap</b>
          <p>
            Set a max cap to prevent overpaying.
          </p>
        </template>
        <template v-else>
          <b>Exact fee</b>
        </template>
        <q-input
          v-model="priorityFee.exactFee" placeholder="Enter custom value" clearable suffix="SOL"
          @keypress="onlyNumber"
        />
      </q-card-section>
      <q-card-section>
        <div class="column">
          <q-btn
            color="primary"
            unelevated
            :ripple="false"
            @click="submit"
          >
            Save changes
          </q-btn>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style lang="scss">
.priority-fee-wrap {
  position: relative;
}

.priority-fee {
  //position: absolute;
  //top: -60px;
  //right: 0;
  > .q-btn {
    color: white;
    &:before {
      background: currentColor;
      opacity: 0.05;
    }
    //span {
    //  color: $primary;
    //}
  }

  @media (max-width: $breakpoint-sm) {
    position: relative;
    top: 0;
    // margin: 0 0 20px;
    .q-btn {
      width: 100%;
    }
  }
}
</style>
